import { Link } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import QRCode from 'qrcode'
import {
  Card,
  CardContent,
  Button,
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@lead-magnifier/ui'
import { Download, Users, Settings, MoreVertical, Archive, ExternalLink, Copy, Check } from 'lucide-react'

interface EventWithStats {
  id: string
  name: string
  slug: string
  tenantSlug: string
  startDate: string
  endDate: string
  primaryColor: string
  status: 'draft' | 'active' | 'ended' | 'archived'
  totalLeads: number
  confirmedLeads: number
}

interface EventCardProps {
  event: EventWithStats
  onArchive?: (eventId: string) => void
}

export function EventCard({ event, onArchive }: EventCardProps) {
  const [qrDataUrl, setQrDataUrl] = useState<string>('')
  const [copied, setCopied] = useState(false)

  const formUrl = `${window.location.origin}/form/${event.tenantSlug}/${event.slug}`
  const conversionRate = event.totalLeads > 0
    ? Math.round((event.confirmedLeads / event.totalLeads) * 100)
    : 0

  useEffect(() => {
    QRCode.toDataURL(formUrl, {
      width: 200,
      margin: 2,
      color: {
        dark: event.primaryColor || '#000000',
        light: '#ffffff',
      },
    }).then(setQrDataUrl)
  }, [formUrl, event.primaryColor])

  const downloadQR = async (format: 'png' | 'svg') => {
    if (format === 'png') {
      const link = document.createElement('a')
      link.download = `${event.slug}-qr.png`
      link.href = qrDataUrl
      link.click()
    } else {
      const svg = await QRCode.toString(formUrl, { type: 'svg' })
      const blob = new Blob([svg], { type: 'image/svg+xml' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.download = `${event.slug}-qr.svg`
      link.href = url
      link.click()
      URL.revokeObjectURL(url)
    }
  }

  const copyFormUrl = async () => {
    await navigator.clipboard.writeText(formUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const statusBadge = {
    draft: { label: 'Entwurf', variant: 'outline' as const },
    active: { label: 'Aktiv', variant: 'default' as const },
    ended: { label: 'Beendet', variant: 'secondary' as const },
    archived: { label: 'Archiviert', variant: 'outline' as const },
  }[event.status]

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('de-AT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      <CardContent className="p-0">
        {/* QR Code Hero */}
        <Link to={`/events/${event.id}`} className="block">
          <div className="flex items-center gap-4 p-4 border-b bg-muted/30">
            <div className="shrink-0">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt={`QR-Code für ${event.name}`}
                  className="w-24 h-24 rounded-lg"
                />
              ) : (
                <div className="w-24 h-24 rounded-lg bg-muted animate-pulse" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-lg truncate">{event.name}</h3>
                <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {formatDate(event.startDate)} – {formatDate(event.endDate)}
              </p>
            </div>
          </div>
        </Link>

        {/* Stats */}
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{event.totalLeads}</span>
              <span className="text-sm text-muted-foreground">Leads</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${conversionRate}%` }}
                />
              </div>
              <span className="text-sm text-muted-foreground ml-1">{conversionRate}%</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="flex-1">
                  <Download className="h-4 w-4 mr-2" />
                  QR-Code
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => downloadQR('png')}>
                  Als PNG herunterladen
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadQR('svg')}>
                  Als SVG herunterladen
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={copyFormUrl}>
                  {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                  {copied ? 'Kopiert!' : 'Link kopieren'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to={`/events/${event.id}/leads`} className="flex-1">
              <Button variant="outline" size="sm" className="w-full">
                <Users className="h-4 w-4 mr-2" />
                Leads
              </Button>
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to={`/events/${event.id}/settings`}>
                    <Settings className="h-4 w-4 mr-2" />
                    Einstellungen
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href={formUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Formular öffnen
                  </a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onArchive?.(event.id)}
                  className="text-muted-foreground"
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Archivieren
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
